class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    if(this.querySelector('[data-predictive-search]')){
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.predictiveSearchStatus = this.querySelector('.predictive-search-status');
    }
    else{
      this.predictiveSearchResults = this.closest('.search-parent-js').querySelector('[data-predictive-search]');
      this.predictiveSearchStatus = this.closest('.search-parent-js').querySelector('.predictive-search-status');
    }

    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    document.querySelector('.predictive-search__loading-state.hide') && document.querySelector('.predictive-search__loading-state.hide').classList.remove('hide');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange(e) {
    this.searchTerm = this.getQuery();

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();

    // Multi-SKU: when the query looks like SKUs (all-caps, digits, hyphens),
    // let sku-search.js handle it — it uses the Storefront API to show all matching products.
    // Do not redirect here, as that would only pass the first SKU to the results page.
    const query = this.getQuery();
    const fragments = query.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    const looksLikeSku = (s) => s.length <= 20 && /^[A-Z0-9\-_]+$/.test(s);
    if (fragments.length > 1 && fragments.every(looksLikeSku)) {
      event.preventDefault();
      // sku-search.js has its own submit listener that will run and handle this query
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if(this.classList.contains('static') && this.querySelector('.field__input').value != "" ) return false;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Multi-SKU: if input has multiple space/comma separated tokens (e.g. "102-304 109-285")
    // run one request per token and merge the HTML results.
    // IMPORTANT: Shopify Predictive Search API cannot match multiple SKUs in one query —
    // it treats the full string as one term, finds no product that matches all of them,
    // and returns nothing.  We split and fire one request per SKU, then merge.
    const fragments = searchTerm.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (fragments.length > 1) {
      const skuUrl = (q) => `${routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[options][fields]=variants.sku,title,product_type,vendor,body&resources[options][prefix]=last&section_id=predictive-search`;

      Promise.all(fragments.map(f =>
        fetch(skuUrl(f))
          .then(r => r.ok ? r.text() : '')
          .catch(() => '')
      ))
        .then(htmlResults => {
          // Parse each result and collect unique product list items by href.
          // NOTE: Shopify's liquid section is gated on {%- if predictive_search.performed -%}
          // so when a SKU returns no results the section outputs *nothing* — the wrapper
          // div #shopify-section-predictive-search does not exist in the response.
          // We fall back to searching the whole parsed document so we never silently skip.
          const seenHrefs = new Set();
          const allItems = [];

          htmlResults.forEach(html => {
            if (!html) return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const root = doc.querySelector('#shopify-section-predictive-search') || doc.documentElement;
            root.querySelectorAll('.predictive-search__list-item').forEach(li => {
              const a = li.querySelector('a');
              const href = a ? a.getAttribute('href') : null;
              if (href && !seenHrefs.has(href)) {
                seenHrefs.add(href);
                allItems.push(li.outerHTML);
              }
            });
          });

          if (!allItems.length) {
            // Every individual SKU search returned nothing — show a clean no-results state
            const noResultsMarkup = `
              <div id="predictive-search-results" role="listbox">
                <div id="predictive-search-option-search-keywords" class="predictive-search-option-search-keywords" role="option" aria-selected="false">
                  <button id="search-for-button" class="link link-hover-span body2" tabindex="-1">
                    <span>Search for "${searchTerm}"</span>
                  </button>
                </div>
                <div class="predictive-search__loading-state" aria-hidden="true"></div>
              </div>
              <span class="hidden" data-predictive-search-live-region-count-value>0 products found</span>`;
            this.allPredictiveSearchInstances.forEach(inst => { inst.cachedResults[queryKey] = noResultsMarkup; });
            this.renderSearchResults(noResultsMarkup);
            return;
          }

          // Build merged markup reusing the theme's existing structure
          const markup = `
            <div id="predictive-search-results" role="listbox">
              <div class="predictive-search__results-groups-wrapper">
                <div class="predictive-search__result-group">
                  <p id="predictive-search-products" class="predictive-search__heading body2">
                    Products
                  </p>
                  <ul id="predictive-search-results-list" class="predictive-search__results-list unstyle-ul" role="listbox" aria-labelledby="predictive-search-products">
                    ${allItems.join('')}
                  </ul>
                </div>
              </div>
              <div id="predictive-search-option-search-keywords" class="predictive-search-option-search-keywords" role="option" aria-selected="false">
                <button id="search-for-button" class="link link-hover-span body2" tabindex="-1">
                  <span>Search for "${searchTerm}"</span>
                </button>
              </div>
              <div class="predictive-search__loading-state" aria-hidden="true"></div>
            </div>
            <span class="hidden" data-predictive-search-live-region-count-value>${allItems.length} product${allItems.length !== 1 ? 's' : ''} found</span>`;

          this.allPredictiveSearchInstances.forEach(inst => { inst.cachedResults[queryKey] = markup; });
          this.renderSearchResults(markup);
        })
        .catch(err => { console.error('[sku-multi]', err); this.close(); });
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[options][fields]=variants.sku,title,product_type,vendor,body&resources[options][prefix]=last&section_id=predictive-search`, {
      signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.predictiveSearchStatus;
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    var count_value = false;
    if(this.querySelector('[data-predictive-search-live-region-count-value]')){
      count_value = this.querySelector('[data-predictive-search-live-region-count-value]');
    }
    else{
      count_value = this.closest('.search-parent-js').querySelector('[data-predictive-search-live-region-count-value]');
    }
    this.setLiveRegionText(count_value.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - this.getBoundingClientRect().bottom - 50;
    return this.resultsMaxHeight;
  }

  open() {
    if(!this.closest('.search-parent-js')){
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);